import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  effect,
  ElementRef,
  EnvironmentInjector,
  inject,
  input,
  runInInjectionContext,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatRippleModule } from '@angular/material/core';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Capacitor } from '@capacitor/core';
import { Share } from '@capacitor/share';
import {
  TransitionView,
  TransitionViewContainer,
} from '@components/transition-view/transition-view.component';
import {
  UserList,
  UserListAction,
  UserListModel,
} from '@components/user-list/user-list.component';
import { HttpStoreFacade } from '@http/store/facade';
import { ANON_USER } from '@http/store/user/user-state';
import { PuebloRouter } from '@routing/pueblo-router.service';
import { ColorService } from '@services/color.service';
import { PlatformService } from '@services/platform.service';
import { PuebloDialog, PuebloDialogs } from '@services/pueblo-dialogs.service';
import { DEFAULT_COLOR } from '@utils/defaults';
import { onObsConfirmed } from '@utils/rxjs-utils';
import { getTopRelevantUsers } from '@utils/user-utils';
import { QRCodeComponent } from 'angularx-qrcode';
import {
  HangResourceName,
  HexColor,
  PuebloResourceName,
  User,
  UserResourceName,
} from 'pueblo-types';
import {
  BehaviorSubject,
  combineLatest,
  defer,
  first,
  interval,
  map,
  of,
  switchMap,
  take,
} from 'rxjs';
import { DisableAutofocus } from '../../forms/disable-autofocus/disable-autofocus.component';

const MIN_LOGO_WIDTH = 60;
const MIN_LOGO_HEIGHT = 70;
const MIN_LOGO_HEIGHT_EMOJI_ONLY = 60;
const QR_CODE_WIDTH = 250;
const MAX_INVITE_LIST_USERS = 20;

enum View {
  MAIN = 'main',
  QR_CODE = 'qrCode',
}

@Component({
  selector: 'invite-dialog',
  templateUrl: 'invite-dialog.component.html',
  styleUrl: 'invite-dialog.component.scss',
  standalone: true,
  imports: [
    DisableAutofocus,
    MatButtonModule,
    MatIconModule,
    MatDialogModule,
    MatMenuModule,
    MatRippleModule,
    NgTemplateOutlet,
    QRCodeComponent,
    TransitionView,
    TransitionViewContainer,
    UserList,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InviteDialog {
  protected readonly changeDetectorRef = inject(ChangeDetectorRef);
  protected readonly colorService = inject(ColorService);
  protected readonly dialogRef = inject(MatDialogRef);
  protected readonly httpStore = inject(HttpStoreFacade);
  protected readonly injector = inject(EnvironmentInjector);
  protected readonly platform = inject(PlatformService);
  protected readonly puebloRouter = inject(PuebloRouter);
  protected readonly puebloDialogs = inject(PuebloDialogs);
  protected readonly snackbar = inject(MatSnackBar);

  protected readonly Capacitor = Capacitor;
  protected readonly View = View;

  protected readonly views = [View.MAIN, View.QR_CODE];
  protected readonly $currentView = signal(View.MAIN);

  readonly $url = input.required<string>({ alias: 'url' });
  readonly $emoji = input<string>('', { alias: 'emoji' });
  readonly $entityDisplayName = input.required<string>({
    alias: 'entityDisplayName',
  });
  readonly $entityResourceName = input.required<
    PuebloResourceName | HangResourceName
  >({
    alias: 'entityResourceName',
  });
  readonly $entityType = input.required<'hang' | 'pueblo'>({
    alias: 'entityType',
  });
  readonly $puebloBelowEmoji = input(false);
  readonly $omitUsers = input<UserResourceName[]>([], {
    alias: 'omitUsers',
  });

  readonly $urlNoHttp = computed<string>(() =>
    this.$url().replace(/^https?:\/\//, ''),
  );
  readonly $shareSmsText = input.required<string>({ alias: 'shareSmsText' });

  protected readonly $qrCodeComponent =
    viewChild.required<QRCodeComponent>('qrCode');

  protected readonly $screenshotMode = signal(false);

  protected readonly usersLoading$ = new BehaviorSubject<Set<UserResourceName>>(
    new Set(),
  );
  protected readonly usersInvited$ = new BehaviorSubject<Set<UserResourceName>>(
    new Set(),
  );

  protected readonly $users = toSignal(
    this.httpStore.appUser$.pipe(
      switchMap((appUser) =>
        combineLatest({
          appUser: of(appUser),
          ownedPueblos: this.httpStore.pueblos$(appUser.ownedPueblos),
          joinedPueblos: this.httpStore.pueblos$(appUser.joinedPueblos),
          omitUsers: toObservable(this.$omitUsers),
        }),
      ),
      switchMap(({ appUser, ownedPueblos, joinedPueblos, omitUsers }) => {
        const topRelevantUsers = getTopRelevantUsers(appUser);

        const puebloUsers = new Set<UserResourceName>();
        for (const pueblo of ownedPueblos) {
          for (const user of pueblo.members) {
            if (topRelevantUsers.includes(user)) continue;
            puebloUsers.add(user);
          }
        }
        for (const pueblo of joinedPueblos) {
          for (const user of pueblo.owners) {
            if (topRelevantUsers.includes(user)) continue;
            puebloUsers.add(user);
          }
        }
        return this.httpStore.users$(
          [...topRelevantUsers, ...puebloUsers]
            .filter((user) => !omitUsers.includes(user))
            .slice(0, MAX_INVITE_LIST_USERS),
        );
      }),
    ),
    { initialValue: [] },
  );

  protected readonly $userModels = toSignal(
    combineLatest({
      users: toObservable(this.$users),
      omitUsers: toObservable(this.$omitUsers),
      usersLoading: this.usersLoading$,
      usersInvited: this.usersInvited$,
    }).pipe(
      map(({ users, usersLoading, usersInvited }): UserListModel[] => {
        return users.map((user) => {
          const action: UserListAction = usersInvited.has(user.resourceName)
            ? {
                icon: 'done',
                onClick: this.undoSendAction,
              }
            : {
                label: 'Invite',
                onClick: this.userClickAction,
              };
          return {
            user,
            loading: usersLoading.has(user.resourceName),
            action,
          } as UserListModel;
        });
      }),
    ),
    { initialValue: [] },
  );

  protected readonly $appUser = toSignal(this.httpStore.appUser$, {
    initialValue: ANON_USER,
  });

  protected readonly $logoGapWidth = signal(MIN_LOGO_WIDTH);
  protected readonly $logoGapHeight = computed(() => {
    return this.$emoji() && !this.$puebloBelowEmoji()
      ? MIN_LOGO_HEIGHT_EMOJI_ONLY
      : MIN_LOGO_HEIGHT;
  });

  protected readonly userClickAction = (user: User): void => {
    this.usersLoading$.next(
      new Set([...this.usersLoading$.value, user.resourceName]),
    );
    const endLoading = () => {
      this.usersLoading$.next(
        new Set(
          [...this.usersLoading$.value].filter((u) => u !== user.resourceName),
        ),
      );
    };
    if (this.$entityType() === 'hang') {
      this.httpStore
        .hang$(this.$entityResourceName() as HangResourceName)
        .pipe(first())
        .subscribe((hang) => {
          this.httpStore.sendHangInvite(hang, user.resourceName, {
            onSuccess: () => {
              onObsConfirmed(
                this.httpStore.hang$(hang.resourceName),
                (updatedHang) => {
                  return updatedHang.sharedWithUsers.includes(
                    user.resourceName,
                  );
                },
              ).subscribe({
                next: () => {
                  this.usersInvited$.next(
                    new Set([...this.usersInvited$.value, user.resourceName]),
                  );
                  endLoading();
                },
                error: (e) => endLoading(),
              });
            },
            onFailure: () => endLoading(),
          });
        });
    }
  };

  protected readonly undoSendAction = (user: User): void => {
    const click = () => {
      this.usersLoading$.next(
        new Set([...this.usersLoading$.value, user.resourceName]),
      );
      const endLoading = () => {
        this.usersLoading$.next(
          new Set(
            [...this.usersLoading$.value].filter(
              (u) => u !== user.resourceName,
            ),
          ),
        );
      };
      if (this.$entityType() === 'hang') {
        this.httpStore
          .hang$(this.$entityResourceName() as HangResourceName)
          .pipe(first())
          .subscribe((hang) => {
            this.httpStore.revokeHangInvite(hang, user.resourceName, {
              onSuccess: () => {
                onObsConfirmed(
                  this.httpStore.hang$(hang.resourceName),
                  (updatedHang) => {
                    return !updatedHang.sharedWithUsers.includes(
                      user.resourceName,
                    );
                  },
                ).subscribe({
                  next: () => {
                    this.usersInvited$.next(
                      new Set(
                        [...this.usersInvited$.value].filter(
                          (u) => u != user.resourceName,
                        ),
                      ),
                    );
                    endLoading();
                  },
                  error: (e) => endLoading(),
                });
              },
              onFailure: () => endLoading(),
            });
          });
      }
    };

    this.puebloDialogs.open(PuebloDialog.SIMPLE, {
      title: 'Unsend Invite?',
      contentStr: `Are you sure you want to unsend this invite to ${user.displayName}?`,
      rightAction: {
        type: 'flat',
        label: 'Unsend',
        error: true,
        close: true,
        click,
      },
      leftAction: {
        type: 'default',
        label: 'Never mind',
        close: true,
      },
    });
  };

  protected readonly $title = computed<string>(() => {
    return this.$entityType() === 'hang'
      ? `Join ${this.$appUser()?.displayName}'s ${this.$entityDisplayName()} Hang!`
      : `Subscribe to ${this.$appUser()?.displayName}'s ${this.$entityDisplayName()} Pueblo on Pueblo!`;
  });

  protected getPuebloContextTitle() {
    return `Subscribe to ${this.$appUser()?.displayName}'s ${this.$entityDisplayName()} Pueblo on Pueblo!`;
  }

  protected getPuebloContextSubtitle() {
    return `Get notified about upcoming hangs!`;
  }

  private readonly $qrCodeContainer =
    viewChild<ElementRef<HTMLElement>>('qrCodeContainer');

  readonly $qrCodeColor = computed<HexColor>(() => {
    if (this.$screenshotMode()) return '#000000';
    const appUser = this.$appUser();
    const userColor = appUser?.color ?? DEFAULT_COLOR;
    const theme = this.colorService.$theme();
    return theme === 'dark'
      ? this.colorService.getColor({
          hex: userColor,
          scheme: appUser.preferences.colorScheme,
          theme: appUser.preferences.theme,
          tone: 80,
        })
      : this.colorService.getColor({
          hex: userColor,
          scheme: appUser.preferences.colorScheme,
          theme: appUser.preferences.theme,
          tone: 20,
        });
  });

  readonly $qrBackgroundColor = computed<HexColor>(() => {
    if (this.$screenshotMode()) return '#FFFFFF';
    const appUser = this.$appUser();
    const userColor = appUser?.color ?? DEFAULT_COLOR;
    const theme = this.colorService.$theme();
    return theme === 'dark'
      ? this.colorService.getColor({
          hex: userColor,
          scheme: appUser.preferences.colorScheme,
          theme: appUser.preferences.theme,
          tone: 10,
        })
      : this.colorService.getColor({
          hex: userColor,
          scheme: appUser.preferences.colorScheme,
          theme: appUser.preferences.theme,
          tone: 96,
        });
  });

  readonly $logoColor = computed<HexColor>(() => {
    const appUser = this.$appUser();
    const userColor = appUser?.color ?? DEFAULT_COLOR;
    const theme = this.colorService.$theme();
    return theme === 'dark'
      ? this.colorService.getColor({
          hex: userColor,
          scheme: appUser.preferences.colorScheme,
          theme: appUser.preferences.theme,
          tone: 92,
        })
      : this.colorService.getColor({
          hex: userColor,
          scheme: appUser.preferences.colorScheme,
          theme: appUser.preferences.theme,
          tone: 60,
        });
  });

  protected readonly $canShare = toSignal(
    defer(async () => (await Share.canShare()).value),
  );

  constructor() {
    effect(() => {
      const screenshotMode = this.$screenshotMode();

      if (screenshotMode) {
        this.dialogRef.addPanelClass('fullscreen-dialog');
      } else {
        this.dialogRef.removePanelClass('fullscreen-dialog');
      }
    });

    // Ensure change detection runs for a few cycles to ensure the QR code is
    // rendered in the correct color scheme.
    effect(() => {
      this.$screenshotMode();
      interval(1)
        .pipe(take(10))
        .subscribe(() => {
          this.changeDetectorRef.detectChanges();
        });
    });
  }

  ngAfterViewInit() {
    runInInjectionContext(this.injector, () => {
      effect(() => {
        const qrCodeContainer = this.$qrCodeContainer();
        if (!qrCodeContainer) return;

        untracked(() => {
          const svgParent =
            qrCodeContainer.nativeElement.querySelector('.center');
          if (!svgParent) return;

          const observer = new MutationObserver(() => {
            const path = svgParent.firstChild?.lastChild as HTMLElement | null;
            if (!path) return;
            const points = path.getAttribute('d')?.split(' ');
            let max = 0;
            if (points) {
              for (const point of points) {
                if (!point.includes('h')) continue;
                const num = parseInt(point.split('h')[0]);
                if (num > max) max = num;
              }
            }

            const qrWidthSquares = max + 1;
            const squareSizePx = QR_CODE_WIDTH / qrWidthSquares;
            this.addLogoBackgroundCover(qrWidthSquares, squareSizePx);
          });
          observer.observe(svgParent, {
            characterData: false,
            childList: true,
            attributes: false,
          });
        });
      });
    });
  }

  protected async copyShortUrl(): Promise<void> {
    const copySuccess = await navigator.clipboard
      .writeText(this.$url())
      .then(() => true)
      .catch(() => false);
    const snackbarMsg = copySuccess
      ? 'Link copied to clipboard'
      : 'Failed to copy to clipboard';
    this.snackbar.open(snackbarMsg, undefined, { duration: 800 });
  }

  private firstSuitableSize(
    minSize: number,
    qrWidthSquares: number,
    squareSizePx: number,
  ) {
    let size = qrWidthSquares % 2 === 0 ? 0 : squareSizePx;
    while (size < minSize) size += squareSizePx * 2;
    return size;
  }

  private addLogoBackgroundCover(qrWidthSquares: number, squareSizePx: number) {
    const coverWidth = this.firstSuitableSize(
      this.$logoGapWidth(),
      qrWidthSquares,
      squareSizePx,
    );
    const coverHeight = this.firstSuitableSize(
      this.$logoGapHeight(),
      qrWidthSquares,
      squareSizePx,
    );

    const numRows = Math.round(coverWidth / squareSizePx);
    const startRow = (qrWidthSquares - numRows) / 2;
    const numCols = Math.round(coverHeight / squareSizePx);
    const startCol = (qrWidthSquares - numCols) / 2;

    const qrCodeContainer = this.$qrCodeContainer();
    if (!qrCodeContainer) return;

    const existingCover =
      qrCodeContainer.nativeElement.querySelector('#logoCover');
    if (existingCover) {
      existingCover.remove();
    }

    const svg = qrCodeContainer.nativeElement.querySelector('.center')
      ?.firstChild as SVGSVGElement | null;
    if (!svg) return;
    const newPath = document.createElementNS(
      'http://www.w3.org/2000/svg',
      'path',
    );
    newPath.setAttribute('id', 'logoCover');
    newPath.setAttribute(
      'd',
      this.createCoverPathStr(startRow, startCol, numRows, numCols),
    );
    newPath.setAttribute('stroke', this.$qrBackgroundColor());
    svg.appendChild(newPath);
  }

  protected async handleShare(): Promise<void> {
    const appUser = this.$appUser();
    if (!appUser) return;
    await Share.share({
      text: this.$shareSmsText(),
      url: this.$url(),
      dialogTitle: 'Share Hang',
    });
  }

  private createCoverPathStr(
    startRow: number,
    startCol: number,
    numRows: number,
    numCols: number,
  ) {
    const resultParts = [];
    for (let row = startRow; row < startRow + numRows; row++) {
      resultParts.push(`M${row + 0.5} ${startCol}v${numCols}`);
    }
    return resultParts.join(' ');
  }
}
